import { DateRangePicker, Hint, PrimaryButton } from '@systemeio/ui'
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface'
import { useApproveContactsImport } from 'modules/contacts/contacts-import/hook/use-approve-contacts-import'
import { useLastContactsImports } from 'modules/contacts/contacts-import/hook/use-last-contacts-imports'
import {
  ContactsImportStatus,
  ContactsLastImportId,
  ContactsLastImportsApiInterface,
  ContactsLastImportsInterface,
} from 'modules/contacts/contacts-import/types/contacts-import-interface'
import { getInvalidContactsLastImportAllData } from 'modules/contacts/utils/get-invalid-contacts-last-import-all-data-'
import { getInvalidContactsLastImportRows } from 'modules/contacts/utils/get-invalid-contacts-last-import-rows'
import React, { useEffect } from 'react'
import ConfirmationModalWithButton from 'shared/components/confirmation-modal/confirmation-modal-with-button'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { AdminRolesEnum } from 'shared/enums/user-roles-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getMonthDateRange, getToday } from 'shared/utils/get-dates'
import { KeyedMutator } from 'swr'

interface LastContactsImportsTableProps {
  className?: string
  setMutateLastContactsImports: (mutate: KeyedMutator<ContactsLastImportsApiInterface>) => void
}

const defaultFilters: ContactPageFilterInterface = { date: [null, null] }

const LastContactsImportsTable = React.forwardRef<HTMLDivElement, LastContactsImportsTableProps>(
  function LastContactsImportsTable({ className, setMutateLastContactsImports }, ref) {
    const { t } = useLocoTranslation()
    const { user, checkAdminRoles, intlLocale, isSupportImpersonation } = useUser()

    const { setPagination, pagination, setLimit, limit, filter, setFilter } = useFilterPagination(
      defaultFilters,
      true,
    )

    const { fetcherApproveContactsImport } = useApproveContactsImport()
    const { lastContactsImports, mutateLastContactsImports, isLastContactsImportsValidating } =
      useLastContactsImports({
        limit,
        pagination,
        filter,
      })

    const handleApproveContactsImport = async (id: ContactsLastImportId) => {
      await fetcherApproveContactsImport(id)
      await mutateLastContactsImports(data => ({
        hasMore: data?.hasMore || false,
        items:
          data?.items.map(item => ({
            ...item,
            status: ContactsImportStatus.approved,
          })) || [],
      }))
    }

    const columns: ColumnDefinitionInterface<ContactsLastImportsInterface>[] = [
      {
        key: 'date',
        header: t('dashboard.contact_import.history.table.header.date'),
        widthPercent: 33,
        onRender: data => (
          <span>
            {dateTimeFormat(data.date, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        ),
      },
      {
        key: 'registered',
        header: t('dashboard.contact_import.history.table.header.registered'),
        widthPercent: 33,
        onRender: data => <span>{`${data.processedContactsCount}/${data.contactsCount}`}</span>,
      },
      {
        key: 'status',
        header: t('dashboard.contact_import.history.table.header.status'),
        widthPercent: 33,
        onRender: data => {
          switch (data.status) {
            case ContactsImportStatus.approved: {
              return t('dashboard.contact_import.history.table.status.approved')
            }
            case ContactsImportStatus.failed: {
              return t('dashboard.contact_import.history.table.status.failed')
            }
            case ContactsImportStatus.suspended: {
              if (
                checkAdminRoles([
                  AdminRolesEnum.admin,
                  AdminRolesEnum.superAdmin,
                  AdminRolesEnum.support,
                ]) ||
                isSupportImpersonation
              ) {
                return (
                  <ConfirmationModalWithButton
                    onConfirm={() => handleApproveContactsImport(data.id)}
                    title={t(
                      'dashboard.contact_import.history.table.approve_action.confirmation_dialog.title',
                    )}
                    confirmationText={t(
                      'dashboard.contact_import.history.table.approve_action.confirmation_dialog.message',
                    )}
                    renderButton={props => (
                      <PrimaryButton {...props}>
                        {t('dashboard.contact_import.history.table.body.approve_button')}
                      </PrimaryButton>
                    )}
                  />
                )
              } else {
                return (
                  <Hint
                    label={t('dashboard.contact_import.history.table.status.tooltip_for_suspended')}
                  >
                    {t('dashboard.contact_import.history.table.status.suspended')}
                  </Hint>
                )
              }
            }
          }
        },
      },
      {
        key: 'invalidData',
        header: (
          <Hint label={t('dashboard.contact_import.history.table.header.invalid_data_hint')}>
            {t('dashboard.contact_import.history.table.header.invalid_data')}
          </Hint>
        ),
        widthPercent: 33,
        onRender: ({ id, invalidContactsCount }) =>
          invalidContactsCount ? (
            <a className="primary-link" download href={getInvalidContactsLastImportRows(id)}>
              {t('dashboard.contact_import.history.table.header.invalid_data_download', {
                count: invalidContactsCount,
              })}
            </a>
          ) : (
            <p>{t('dashboard.contact_import.history.table.cell.no_invalid_rows')}</p>
          ),
      },
      ...(checkAdminRoles([
        AdminRolesEnum.admin,
        AdminRolesEnum.superAdmin,
        AdminRolesEnum.support,
      ]) || isSupportImpersonation
        ? [
            {
              key: 'allData',
              header: t('dashboard.contact_import.history.table.header.all_data'),
              widthPercent: 33,
              onRender: ({ id }) => (
                <a className="primary-link" download href={getInvalidContactsLastImportAllData(id)}>
                  {t('dashboard.contact_import.history.table.body.all_data_download')}
                </a>
              ),
            } as ColumnDefinitionInterface<ContactsLastImportsInterface>,
          ]
        : []),
    ]

    useEffect(() => {
      setMutateLastContactsImports(mutateLastContactsImports)
    }, [setMutateLastContactsImports, mutateLastContactsImports])

    useEffect(() => {
      setFilter({ date: getMonthDateRange(user?.timezone) })
    }, [setFilter, user?.timezone])

    return (
      <div className={className} ref={ref}>
        <ListingLayout
          counter={false}
          data={isLastContactsImportsValidating ? undefined : lastContactsImports}
          type={'default'}
          title={t('dashboard.contact_import.history.table.title')}
          customHeaderElement={
            <DateRangePicker
              maxDate={getToday(user?.timezone)}
              getDefaultDate={() => getMonthDateRange(user?.timezone)}
              date={filter.date}
              onChange={date => {
                setPagination({ startFromId: undefined, order: OrderEnum.next })
                setLimit(10)
                setFilter({ date: date })
              }}
              timezone={user?.timezone}
              createdAt={user?.createdAt}
              intlLocale={intlLocale}
            />
          }
        >
          <Table
            limiter={{ limit, setLimit }}
            paginator={{ pagination, setPagination }}
            data={isLastContactsImportsValidating ? undefined : lastContactsImports}
            columns={columns}
          />
        </ListingLayout>
      </div>
    )
  },
)

export default LastContactsImportsTable
